import { PureComponent } from 'react';
import { defaultMemoize } from 'reselect';
import PropTypes from 'prop-types';
import elementType from 'prop-types-extra/lib/elementType';
import ControlLabel from 'react-bootstrap/lib/ControlLabel';
import FormControl from 'react-bootstrap/lib/FormControl';
import FormGroup from 'react-bootstrap/lib/FormGroup';
import HelpBlock from 'react-bootstrap/lib/HelpBlock';
import CheckIcon from 'components/icons/CheckIconGreen.com.svg';
import cns from 'classnames';

import withRestProps from 'helpers/withRestProps';

import cn from './styles.local.less';

const getInput = defaultMemoize(({ input, setState, transform }) => {
  const { onBlur, onFocus, onChange } = input;

  /**
   * @param {React.SyntheticEvent} event
   */
  function applyTransform(event) {
    event.target.value = transform(event.target.value);

    return onChange(event);
  }

  input = {
    ...input,
    onBlur: (...args) => {
      setState({ isShowExtraNote: true });
      onBlur(...args);
    },
    onFocus: (...args) => {
      setState({ isShowExtraNote: false });
      if (input.onFocus) {
        onFocus(...args);
      }
    },
    ...(typeof transform === 'function'
      ? {
          onChange: applyTransform,
        }
      : {}),
  };

  return input;
});

class Group extends PureComponent {
  static propTypes = {
    ControlLabel: elementType,
    FormControl: elementType,
    FormGroup: elementType,
    bsSize: PropTypes.string,
    className: PropTypes.string,
    controlId: PropTypes.string,
    disabled: PropTypes.bool,
    extraNoteNode: PropTypes.node,
    input: PropTypes.object.isRequired,
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    meta: PropTypes.object.isRequired,
    renderHelpBlock: PropTypes.func,
    renderExtraLabel: PropTypes.func,
    renderFormControl: PropTypes.func,
    extraLabel: PropTypes.string,
    required: PropTypes.bool,
    inputWithCheck: PropTypes.bool,
    rest: PropTypes.object,
    sublabel: PropTypes.string,
    hasError: PropTypes.bool,
    transform: PropTypes.func,
  };

  static defaultProps = {
    renderHelpBlock: ({ meta: { error, submitFailed } }) =>
      submitFailed && error ? <HelpBlock>{error}</HelpBlock> : null,
    renderExtraLabel: ({ required }) => (required ? '*' : ''),
    ControlLabel,
    FormControl: ({ input, ...props }) => <FormControl {...input} {...props} />,
    FormGroup: ({
      labelNode,
      extraLabel,
      controlNode,
      helpNode,
      extraNoteNode,
      ...props
    }) => (
      <FormGroup {...props}>
        {labelNode}
        {extraLabel}
        {controlNode}
        {helpNode}
        {extraNoteNode}
      </FormGroup>
    ),
    label: '',
    required: false,
  };

  state = {
    isShowExtraNote: true,
  };

  defaultRenderFormControl = ({
    disabled,
    label,
    FormControl,
    input,
    required,
    hasError,
    rest,
    transform,
  }) => {
    return (
      <FormControl
        {...{ ...rest, hasError }}
        data-mkt-selector={rest.mktSelector}
        data-qa-selector={rest.qaSelector}
        disabled={disabled}
        input={getInput({
          input,
          setState: (...args) => this.setState(...args),
          transform,
        })}
        required={required}
        className={cn.formControl}
        label={label}
      />
    );
  };

  renderExtraNote = () => {
    const { extraNoteNode } = this.props;
    const { isShowExtraNote } = this.state;

    return isShowExtraNote && extraNoteNode;
  };

  renderLabel = () => {
    const { controlId, ControlLabel, disabled, label, extraLabel } = this.props;

    return label ? (
      <ControlLabel
        className={cns({ [cn.disabled]: disabled })}
        disabled={disabled}
        htmlFor={controlId}
      >
        {label}
        {extraLabel}
      </ControlLabel>
    ) : null;
  };

  render() {
    const {
      bsSize,
      className,
      controlId,
      FormGroup,
      renderHelpBlock,
      renderFormControl = this.defaultRenderFormControl,
      renderExtraLabel,
      meta: { error, submitFailed },
      inputWithCheck,
      sublabel,
      input,
      hasError,
    } = this.props;

    const validationState =
      (error && submitFailed) || hasError ? 'error' : null;

    return (
      <div className={cn.root}>
        <FormGroup
          bsSize={bsSize}
          className={className}
          controlId={controlId}
          validationState={validationState}
          labelNode={this.renderLabel()}
          controlNode={renderFormControl(this.props)}
          helpNode={renderHelpBlock(this.props)}
          extraNoteNode={this.renderExtraNote()}
          extraLabel={renderExtraLabel(this.props)}
        />
        {inputWithCheck && input.value && (
          <CheckIcon className={cn.checkIcon} />
        )}
        {sublabel && (
          <div className={cn.sublabel}>
            <span>{sublabel || 'sub label'}</span>
          </div>
        )}
      </div>
    );
  }
}

export default withRestProps()(Group);
